import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

/** The log is read in longer runs: 50 rows by default, like the API. */
export const AUDIT_PAGE_SIZES = [50, 100, 200] as const
type PageSize = (typeof AUDIT_PAGE_SIZES)[number]

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

/**
 * The audit log's filters live in the URL (shareable, back-button safe); invalid values fall back
 * to no filter. Entity types and actions follow the API's own patterns.
 */
export const auditSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is PageSize => (AUDIT_PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
  entityType: z
    .string()
    .regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/)
    .optional()
    .catch(undefined),
  entityId: z.string().trim().min(1).max(100).optional().catch(undefined),
  actorUserId: z.uuid().optional().catch(undefined),
  action: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{0,49}$/)
    .optional()
    .catch(undefined),
  from: isoDate,
  to: isoDate,
})
export type AuditSearch = z.infer<typeof auditSearchSchema>

export interface AuditListParams {
  page: number
  pageSize: number
  entityType: string | undefined
  entityId: string | undefined
  actorUserId: string | undefined
  action: string | undefined
  from: string | undefined
  to: string | undefined
}

export function toAuditListQuery(search: AuditSearch): AuditListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? AUDIT_PAGE_SIZES[0],
    entityType: search.entityType,
    entityId: search.entityId,
    actorUserId: search.actorUserId,
    action: search.action,
    from: search.from,
    to: search.to,
  }
}
